import { type ReactElement, useState } from 'react';

import { readLastSignin } from '@/shared/lib/last-signin';
import { startSocialLogin } from '@/shared/lib/start-social-login';

import { KakaoMark, NaverMark } from './provider-mark';

/**
 * 카카오 · 네이버 로그인 버튼.
 *
 * 이 두 버튼만 사이트 디자인 시스템 밖에 있다. 브랜드 가이드가 배경색·심볼·문구를
 * 지정하기 때문이다 — 카카오는 #FEE500 에 검정 85% 글자, 네이버는 #03C75A 에 흰 글자다.
 * 높이·모서리만 사이트 폼(h-12 rounded-md)에 맞춰 다른 입력 요소와 줄을 세운다.
 *
 * 제공사는 ERD `social_members.provider` 의 ENUM(KAKAO, NAVER)과 1:1로 맞춘다.
 *
 * 문구도 임의로 쓸 수 없어 각 사가 허용한 라벨 중에서 고른다.
 */
type Intent = 'login' | 'signup';

const LABELS: Record<Intent, { kakao: string; naver: string }> = {
  login: { kakao: '카카오 로그인', naver: '네이버 로그인' },
  signup: { kakao: '카카오로 시작하기', naver: '네이버로 시작하기' },
};

/** 라벨은 가운데, 심볼은 왼쪽 끝 고정 — 두 버튼의 글자 중심이 어긋나지 않게 한다. */
const BASE =
  'relative flex h-12 w-full items-center justify-center rounded-md ' +
  'text-[15px] font-semibold tracking-[-0.014em] transition-opacity hover:opacity-90 ' +
  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring';

/**
 * 누르면 제공자의 동의 화면으로 나간다.
 *
 * `intent` 는 이제 문구만 고른다 — 가입인지 로그인인지는 제공자가 준 계정을 보고
 * **서버가** 판정하고, 콜백이 `?status=` 로 알려준다. 화면이 미리 정할 수 없다.
 *
 * 돌아올 곳은 나가기 직전에 기억해 둔다 (`startSocialLogin`).
 *
 * 마지막으로 들어온 수단에는 표를 달아 준다 — 이메일이 다르면 다른 회원이라,
 * 어느 문으로 들어왔는지 기억하지 못하면 사용자는 자기 계정을 잃은 것처럼 느낀다.
 */
export function SocialLoginButtons({
  intent,
  next = '/library',
}: {
  intent: Intent;
  next?: string;
}): ReactElement {
  const label = LABELS[intent];
  // 표식은 화면이 뜬 시점의 값이면 충분하다 — 이 화면에서 바뀔 일이 없다.
  const [lastSignin] = useState(readLastSignin);

  return (
    <div className="flex flex-col gap-2">
      <button
        type="button"
        onClick={() => startSocialLogin('kakao', next)}
        className={`${BASE} bg-[#FEE500] text-[rgba(0,0,0,0.85)]`}
      >
        <KakaoMark className="absolute left-4 size-[18px]" />
        {label.kakao}
        {lastSignin === 'kakao' ? <LastSigninBadge className="bg-black/10" /> : null}
      </button>

      <button
        type="button"
        onClick={() => startSocialLogin('naver', next)}
        className={`${BASE} bg-[#03C75A] text-white`}
      >
        <NaverMark className="absolute left-4 size-[18px]" />
        {label.naver}
        {lastSignin === 'naver' ? <LastSigninBadge className="bg-white/25" /> : null}
      </button>
    </div>
  );
}

/** 배경색이 제공사마다 달라 대비를 각각 맞춘다 — 카카오는 노란 면, 네이버는 초록 면이다. */
function LastSigninBadge({ className }: { className: string }): ReactElement {
  return (
    <span
      className={`absolute right-4 rounded-full px-2 py-0.5 text-[11px] font-medium ${className}`}
    >
      마지막 로그인
    </span>
  );
}
